import type { LHCDetails } from '@diamond/shared/types';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  SearchLHCItemMutationsParams,
  UpdateLHCItemRemarkPayload,
  UpdateLHCItemRemarkResponse,
} from '../api';
import {
  addLHCItems,
  allocateLHCItemMutations,
  createLHC,
  deleteLHC,
  deleteLHCItem,
  duplicateLHC,
  getCollectionMutationDetail,
  getCollectionPaymentProofDetail,
  getRelatedDeliveryOrders,
  reorderLHCItem,
  replaceLHCItemMutations,
  searchLHCDeliveryOrders,
  searchLHCItemMutations,
  updateLHC,
  updateLHCItemRemark,
} from '../api';
import { COLLECTION_QUERY_KEYS } from '../api/constants';
import type {
  CreateLHCPayload,
  DuplicateLHCPayload,
  UpdateLHCPayload,
} from '../types';
import { captureLhcApiError } from '../utils/capture-lhc-api-error';

function shouldUseMockSearchMutations() {
  return process.env.NEXT_PUBLIC_USE_MOCK_SEARCH_MUTATIONS === 'true';
}

type MutationAllocation = {
  bank_mutation_id: string;
  amount_applied: number;
};

type AllocateReplaceVariables = {
  lhcId: string;
  itemId: string;
  allocations: MutationAllocation[];
  lhc_no?: string;
  mutation_nos?: string[];
};

export function useCreateLHCMutation(options?: {
  onSuccess?: (data: LHCDetails) => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateLHCPayload) =>
      createLHC(session.access_token, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_LIST],
      });
      options?.onSuccess?.(data);
    },
    onError: (error, variables) => {
      captureLhcApiError(error, {
        action: 'createLhc',
        extras: {
          lhc_no: variables.lhc_no,
          collection_plant_id: variables.collection_plant_id,
          lhc_date: variables.lhc_date,
        },
      });
      options?.onError?.(error);
    },
  });
}

export function useUpdateLHCMutation(options?: {
  onSuccess?: (data: LHCDetails) => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateLHCPayload }) =>
      updateLHC(session.access_token, id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_LIST],
      });
      options?.onSuccess?.(data);
    },
    onError: (error, { id, payload }) => {
      captureLhcApiError(error, {
        action: 'updateLhc',
        extras: {
          lhcId: id,
          lhc_no: payload.lhc_no,
          collection_plant_id: payload.collection_plant_id,
          lhc_date: payload.lhc_date,
        },
      });
      options?.onError?.(error);
    },
  });
}

export function useSearchLHCDeliveryOrdersMutation(options?: {
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();

  return useMutation({
    mutationFn: ({
      lhcId,
      searchQuery,
    }: {
      lhcId: string;
      searchQuery: string;
    }) => searchLHCDeliveryOrders(session.access_token, lhcId, searchQuery),
    onError: (error, { lhcId, searchQuery }) => {
      captureLhcApiError(error, {
        action: 'searchDeliveryOrders',
        extras: { lhcId, searchQuery },
      });
      options?.onError?.(error);
    },
  });
}

export function useSearchLHCItemMutationsMutation(options?: {
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();

  return useMutation({
    mutationFn: ({
      lhcId,
      itemId,
      params,
    }: {
      lhcId: string;
      itemId: string;
      params: SearchLHCItemMutationsParams;
    }) => {
      if (shouldUseMockSearchMutations()) {
        return import('@diamond/sol-admin/utils').then(
          ({ mockSearchLHCItemMutations }) =>
            mockSearchLHCItemMutations(
              session.access_token,
              lhcId,
              itemId,
              params
            )
        );
      }
      return searchLHCItemMutations(
        session.access_token,
        lhcId,
        itemId,
        params
      );
    },
    onError: (error, { lhcId, itemId, params }) => {
      captureLhcApiError(error, {
        action: 'searchItemMutations',
        extras: {
          lhcId,
          itemId,
          params: params as Record<string, unknown>,
          searchQuery: params.search_query ?? params.mutation_no,
        },
      });
      options?.onError?.(error);
    },
  });
}

export function useCollectionPaymentProofDetailMutation(options?: {
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();

  return useMutation({
    mutationFn: ({
      paymentProofId,
      orderMetaId,
    }: {
      paymentProofId: string;
      orderMetaId: string;
    }) =>
      getCollectionPaymentProofDetail(
        session.access_token,
        paymentProofId,
        orderMetaId
      ),
    onError: (error, { paymentProofId, orderMetaId }) => {
      captureLhcApiError(error, {
        action: 'getPaymentProofDetail',
        extras: { paymentProofId, orderMetaId },
      });
      options?.onError?.(error);
    },
  });
}

export function useCollectionMutationDetailMutation(options?: {
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();

  return useMutation({
    mutationFn: ({
      mutationId,
      orderMetaId,
    }: {
      mutationId: string;
      orderMetaId: string;
    }) =>
      getCollectionMutationDetail(
        session.access_token,
        mutationId,
        orderMetaId
      ),
    onError: (error, { mutationId, orderMetaId }) => {
      captureLhcApiError(error, {
        action: 'getMutationDetail',
        extras: { mutationId, orderMetaId },
      });
      options?.onError?.(error);
    },
  });
}

export function useAllocateLHCItemMutationsMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lhcId,
      itemId,
      allocations,
    }: AllocateReplaceVariables) =>
      allocateLHCItemMutations(session.access_token, lhcId, itemId, {
        allocations,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_DETAIL],
      });
      options?.onSuccess?.();
    },
    onError: (error, { lhcId, itemId, allocations, lhc_no, mutation_nos }) => {
      captureLhcApiError(error, {
        action: 'allocateLhcItemMutations',
        extras: {
          lhcId,
          lhc_no,
          itemId,
          bank_mutation_ids: allocations.map((a) => a.bank_mutation_id),
          mutation_nos,
          allocations,
        },
      });
      options?.onError?.(error);
    },
  });
}

export function useReplaceLHCItemMutationsMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lhcId,
      itemId,
      allocations,
    }: AllocateReplaceVariables) =>
      replaceLHCItemMutations(session.access_token, lhcId, itemId, {
        mode: 'replace',
        allocations,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_DETAIL],
      });
      options?.onSuccess?.();
    },
    onError: (error, { lhcId, itemId, allocations, lhc_no, mutation_nos }) => {
      captureLhcApiError(error, {
        action: 'replaceLhcItemMutations',
        extras: {
          lhcId,
          lhc_no,
          itemId,
          bank_mutation_ids: allocations.map((a) => a.bank_mutation_id),
          mutation_nos,
          allocations,
        },
      });
      options?.onError?.(error);
    },
  });
}

export function useAddLHCItemsMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lhcId,
      deliveryOrderIds,
    }: {
      lhcId: string;
      deliveryOrderIds: string[];
    }) => addLHCItems(session.access_token, lhcId, deliveryOrderIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_DETAIL],
      });
      options?.onSuccess?.();
    },
    onError: (error, { lhcId, deliveryOrderIds }) => {
      captureLhcApiError(error, {
        action: 'addLhcItems',
        extras: { lhcId, deliveryOrderIds },
      });
      options?.onError?.(error);
    },
  });
}

export function useGetRelatedDeliveryOrdersMutation(options?: {
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();

  return useMutation({
    mutationFn: ({
      lhcId,
      poNumber,
      excludeDeliveryOrderId,
    }: {
      lhcId: string;
      poNumber: string;
      excludeDeliveryOrderId?: string;
    }) =>
      getRelatedDeliveryOrders(
        session.access_token,
        lhcId,
        poNumber,
        excludeDeliveryOrderId
      ),
    onError: (error, { lhcId, poNumber, excludeDeliveryOrderId }) => {
      captureLhcApiError(error, {
        action: 'getRelatedDeliveryOrders',
        extras: { lhcId, poNumber, excludeDeliveryOrderId },
      });
      options?.onError?.(error);
    },
  });
}

export function useReorderLHCItemMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lhcId,
      itemId,
      index,
    }: {
      lhcId: string;
      itemId: string;
      index: number;
    }) => reorderLHCItem(session.access_token, lhcId, itemId, index),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_DETAIL],
      });
      options?.onSuccess?.();
    },
    onError: (error, { lhcId, itemId, index }) => {
      captureLhcApiError(error, {
        action: 'reorderLhcItem',
        extras: { lhcId, itemId, index },
      });
      options?.onError?.(error);
    },
  });
}

export function useDeleteLHCItemMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lhcId, itemId }: { lhcId: string; itemId: string }) =>
      deleteLHCItem(session.access_token, lhcId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_DETAIL],
      });
      options?.onSuccess?.();
    },
    onError: (error, { lhcId, itemId }) => {
      captureLhcApiError(error, {
        action: 'deleteLhcItem',
        extras: { lhcId, itemId },
      });
      options?.onError?.(error);
    },
  });
}

export function useDuplicateLHCMutation(options?: {
  onSuccess?: (data: LHCDetails) => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: DuplicateLHCPayload;
    }) => duplicateLHC(session.access_token, id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_LIST],
      });
      options?.onSuccess?.(data);
    },
    onError: (error, { id, payload }) => {
      captureLhcApiError(error, {
        action: 'duplicateLhc',
        extras: {
          id,
          lhc_no: payload.lhc_no,
          plant_id: payload.plant_id,
          lhc_date: payload.lhc_date,
        },
      });
      options?.onError?.(error);
    },
  });
}

export function useDeleteLHCMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteLHC(session.access_token, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_LIST],
      });
      options?.onSuccess?.();
    },
    onError: (error, id) => {
      captureLhcApiError(error, {
        action: 'deleteLhc',
        extras: { lhcId: id },
      });
      options?.onError?.(error);
    },
  });
}

export function useUpdateLHCItemRemarkMutation(options?: {
  onSuccess?: (data: UpdateLHCItemRemarkResponse) => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lhcId,
      itemId,
      payload,
    }: {
      lhcId: string;
      itemId: string;
      payload: UpdateLHCItemRemarkPayload;
    }) => updateLHCItemRemark(session.access_token, lhcId, itemId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_DETAIL],
      });
      options?.onSuccess?.(data);
    },
    onError: (error, { lhcId, itemId, payload }) => {
      captureLhcApiError(error, {
        action: 'updateLhcItemRemark',
        extras: {
          lhcId,
          itemId,
          hasRemark: Boolean(payload.remark && payload.remark.length > 0),
        },
      });
      options?.onError?.(error);
    },
  });
}
